import { type ReactNode, useEffect } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  /** Extra content rendered outside the scrollable panel (e.g. side action bar) */
  sideContent?: ReactNode;
  wide?: boolean;
}

export default function Modal({ isOpen, onClose, children, title, sideContent, wide }: ModalProps) {
  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEsc);

    // Lock background scrolling without shifting the page: freeze the body in
    // place via `position: fixed` while remembering the current scroll
    // position, then restore it on close. Combined with `overflow-y: scroll`
    // on <html> (index.css) the viewport scrollbar slot stays visible, so
    // the fixed header and layout don't jump sideways.
    const body = document.body;
    const scrollY = window.scrollY;
    const previous = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEsc);
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.width = previous.width;
      body.style.overflow = previous.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-200"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 dark:bg-black/70" />

      {/* Wrapper to hold panel + sideContent side by side */}
      <div
        className={`relative z-10 flex gap-3 max-w-full ${wide ? 'h-[90vh]' : 'max-h-[90vh]'}`}
      >
        {/* Panel */}
        <div
          className={`relative flex flex-col overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-gray-800 ${
            wide ? 'w-[56rem] max-w-full h-full' : 'w-full max-w-lg'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          {title && (
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700 shrink-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {title}
              </h2>
              <button
                onClick={onClose}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300 transition-colors cursor-pointer"
              >
                <X size={20} />
              </button>
            </div>
          )}

          {/* Close button when no title */}
          {!title && (
            <button
              onClick={onClose}
              className="absolute top-3 right-3 z-10 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300 transition-colors cursor-pointer"
            >
              <X size={20} />
            </button>
          )}

          {/* Content */}
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-6">{children}</div>
        </div>

        {/* Side content (action bar) */}
        {sideContent && (
          <div
            className="hidden md:flex flex-col shrink-0 self-start"
            onClick={(e) => e.stopPropagation()}
          >
            {sideContent}
          </div>
        )}
      </div>
    </div>
  );
}
